import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './icon-button.scss';

/**
 * @typedef {'default' | 'primary' | 'grey' | 'add' | 'accept' | 'reject' | 'success' | 'warning' | 'white' | 'transparent' | 'warning-hover' | 'bordared-primary' | 'blue' | 'light-blue' | 'transparent-green' | 'delete' | 'purple' | 'purple-bordared' } VerientType
 * @typedef {'default' | 'large' | 'xMedium' | 'medium' | 'small' | 'xSmall' | 's-s-x-medium'} SizeType
 * @typedef {'transparent' | 'default'} BackgroundType
 */

/**
 * Component to demonstrate different sizes, appearances, and popup options.
 * @param {Object} props - Component props.
 * @param {VerientType} props.verient - Variant of the component.
 * @param {SizeType} props.size - Size of the component.
 * @param {React.Ref} ref - Reference to the button element.
 * @returns {JSX.Element} - Rendered component.
 */

const IconButton = forwardRef(
  (
    {
      onClick,
      type,
      verient,
      size,
      disabled,
      margin,
      className,
      active,
      disableHover,
      noPadding,
      notificationCount,
      children,
      background,
      ...props
    },
    ref
  ) => {
    const verientProperties = {
      default: 'default',
      primary: 'primary',
      grey: 'grey',
      add: 'add',
      accept: 'accept',
      reject: 'reject',
      success: 'success',
      warning: 'warning',
      white: 'white',
      delete: 'delete',
      transparent: 'transparent',
      'warning-hover': 'warning-hover',
      'bordared-primary': 'bordared-primary',
      blue: 'blue',
      'light-blue': 'light-blue',
      'transparent-green': 'transparent-green',
      purple: 'purple',
      'purple-bordared': 'purple-bordared',
    };
    const sizeProperties = {
      default: 's-default',
      large: 's-large',
      xMedium: 's-x-medium',
      's-s-x-medium': 's-s-x-medium',
      medium: 's-medium',
      small: 's-small',
      xSmall: 's-x-small',
    };
    const backgroundProperties = {
      transparent: 'transparent',
      default: 'default',
    };
    const verientProps = verientProperties[verient];
    const sizeProps = sizeProperties[size];
    const backgroundProps = backgroundProperties[background];

    return (
      <button
        ref={ref}
        className={`icon-button ${type}${
          className ? ` ${className}` : ''
        }${` ${verientProps}`}${` ${sizeProps}`} ${active ? 'active' : ''}${
          disableHover ? ' disable-hover' : ''
        }${noPadding ? ' p-0' : ''} ${backgroundProps}`}
        type="button"
        style={{ margin }}
        onClick={onClick}
        disabled={disabled}
        {...props}
      >
        {notificationCount && (
          <span className="notification-count">
            {notificationCount !== '0' ? notificationCount : ''}
          </span>
        )}
        <span className="content">{children}</span>
      </button>
    );
  }
);

IconButton.propTypes = {
  type: PropTypes.string,
  size: PropTypes.string,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  verient: PropTypes.string,
  margin: PropTypes.string,
  className: PropTypes.string,
  active: PropTypes.bool,
  disableHover: PropTypes.bool,
  noPadding: PropTypes.bool,
  notificationCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  background: PropTypes.string,
};

IconButton.defaultProps = {
  type: '',
  size: '2.083vw',
  onClick: () => {},
  disabled: false,
  verient: 'default',
  margin: '',
  className: '',
  active: false,
  disableHover: false,
  noPadding: false,
  notificationCount: '',
  background: '',
};

export default IconButton;
